<script lang="ts">
	import CustomCakeBanner from "../components/CustomCake/CustomCakeBanner.svelte";
	import CustomCakeOrderForm from "../components/CustomCake/CustomCakeOrderForm.svelte";
	import Layout from "../components/Layout.svelte";
	import { writable } from "svelte/store";
	import HowItWorks from "../components/HowItWorks.svelte";
	import calendarDays from "../../../core/assets/icons/calendar-days.svg?raw";
	import boxOfCakes from "../../../core/assets/icons/box-of-cakes.svg?raw";
	import phoneOrder from "../../../core/assets/icons/phone-order.svg?raw";
	import type { Step } from "../../../core/schema/Step";
	import Allergies from "../components/Allergies.svelte";
	import bezlaktozaIcon from "../../../core/assets/icons/bezlaktoza-icon.svg";
	import bezlepekIcon from "../../../core/assets/icons/bezlepek-icon.svg";
	import bezlepekIBezlaktozaIcon from "../../../core/assets/icons/bezlaktoza+bezlepek-icon.svg";
	import type { AllergiesProps } from "./CustomCakesProps";
	import { nbspify } from "../../../core/utils/nbspify";
	import { customCakesEnquiryPathMap } from "../../../core/schema/paths/customCakesPathMap";
	import { getGlobalSettings } from "../getGlobalSettings";
	import CustomCakeFillings from "../components/CustomCake/CustomCakeFillings.svelte";
	import Banner from "../components/Banner.svelte";
	import universalCakeImgSm from "../assets/images/custom-cakes/universal-cake.jpg?w=400&webp&imagetools";
	import universalCakeImgMd from "../assets/images/custom-cakes/universal-cake.jpg?w=432&webp&imagetools";
	import universalCakeImgLg from "../assets/images/custom-cakes/universal-cake.jpg?w=496&webp&imagetools";
	import themeCakeImgSm from "../assets/images/custom-cakes/theme-cake.jpg?w=400&webp&imagetools";
	import themeCakeImgMd from "../assets/images/custom-cakes/theme-cake.jpg?w=432&webp&imagetools";
	import themeCakeImgLg from "../assets/images/custom-cakes/theme-cake.jpg?w=496&webp&imagetools";
	import CustomCakeFigurines from "../components/CustomCake/CustomCakeFigurines.svelte";
	import CallToActionPanel from "../components/CallToActionPanel.svelte";
	import { inViewport } from "../utils/inViewport.js";
	import type { CustomCakesProps } from "./CustomCakesProps.js";

	const { enableDeliveryToAddress } = $$restProps as CustomCakesProps;

	const formInViewport = writable(false);

	const { TENANT_LOCALE } = getGlobalSettings();

	const steps: Step[] = [
		{
			title: "Ověříte dostupnost termínu",
			description: "Nejprve se mrkněte do našeho kalendáře, zda máme volný termín.",
			icon: calendarDays,
		},
		{
			title: "Vyplníte jednoduchý formulář",
			description: "V případě volné kapacity budete přesměrování na formulář pro vyplnění potřebných informací.",
			icon: boxOfCakes,
		},
		{
			title: "Zavoláme si a domluvíme detaily",
			description: "Telefonicky se s vámi spojíme, doladíme veškeré detaily a potvrdíme si konečnou cenu.",
			icon: phoneOrder,
		},
	];

	const allergies: AllergiesProps[] = [
		{
			title: "Bez lepku",
			description: "Přidá k ceně dortu nebo zákusků 10 %. Prosím, počítejte s možnými stopami lepku.",
			icon: bezlepekIcon,
		},
		{
			title: "Bez laktózy",
			description:
				"Bezlaktózová úprava přidá k ceně dortu nebo zákusků 20 %. Vše vyrábíme ve stejné výrobně, počítejte tedy s možnými stopami laktózy.",
			icon: bezlaktozaIcon,
		},
		{
			title: "Bez lepku a bez laktózy",
			description: "Přidá k ceně 30 %. Všechny úpravy lze provést jak na jednom patře, tak na celém dortu.",
			icon: bezlepekIBezlaktozaIcon,
		},
	];
</script>

<Layout hasPanel={!$formInViewport}>
	<div class="container">
		<section>
			<CustomCakeBanner />
		</section>

		<section>
			<HowItWorks headline="Jak to probíhá?" {steps} />
		</section>

		<section>
			<Banner
				headline="Univerzální dort"
				subHeadline="Elegantní vzhled, který nadchne každého!"
				image={{
					alt: "Fotografie – ukázka univerzálního dortu",
					sizes: { sm: universalCakeImgSm, md: universalCakeImgMd, lg: universalCakeImgLg },
				}}
			>
				<p>
					{nbspify(
						"Tento dort vždy po obvodu mažeme bílým krémem a zdobíme čerstvým ovocem, makronkami, pusinkami, případně i jménem a věkem oslavence. Dort můžeme ozvláštnit čokoládovými stékanci po obvodu.",
					)}
				</p>
			</Banner>
			<div>
				<div
					class="lg:text-sm mx-auto grid w-fit grid-cols-[repeat(4,auto)] items-center justify-items-center gap-1 text-left text-2xs sm:gap-2 md:gap-x-4 xl:text-md"
				>
					<div class="font-semibold">Počet porcí</div>
					<div class="font-semibold">Cena</div>
					<div class="font-semibold">Rozměry</div>
					<div class="font-semibold">Podložka</div>

					<div>12 porcí</div>
					<div>2600 Kč</div>
					<div class="flex flex-wrap justify-center gap-1"><span>Ø 18 cm,</span> <span>výška 14 cm</span></div>
					<div>30 cm</div>

					<div>18 porcí</div>
					<div>3800 Kč</div>
					<div class="flex flex-wrap justify-center gap-1"><span>Ø 22 cm,</span> <span>výška 14 cm</span></div>
					<div>30 cm</div>

					<div class="flex flex-wrap justify-center gap-1"><span>25 porcí</span> <span>(dvě patra)</span></div>
					<div>4900 Kč</div>
					<div class="flex flex-wrap justify-center gap-1"><span>Ø 22+15 cm,</span> <span>výška 28 cm</span></div>
					<div>30 cm</div>

					<div>Více porcí</div>
					<div>Individuální cena</div>
					<div>-</div>
					<div>-</div>
				</div>
			</div>
		</section>

		<section>
			<Banner
				headline="Tematické dorty"
				subHeadline="Je libo dort pro nadšence do Harryho Pottera, krtečka nebo čehokoli jiného?"
				image={{
					alt: "Fotografie – ukázka tematického dortu",
					sizes: { sm: themeCakeImgSm, md: themeCakeImgMd, lg: themeCakeImgLg },
				}}
			>
				<p>
					{nbspify(
						"Není problém, rádi splníme každé přání. Vaši představu můžeme realizovat pomocí nejrůznějších dekorací. V ceně dortu je jméno a věk oslavence, jedlý tisk, pár makronek, pusinek a čerstvé ovoce. V ceně nejsou zahrnuty modelované figurky, a čokoládové koule. Vnější krém může být bílý nebo barevný.",
					)}
				</p>
				<p class="mt-2">
					{nbspify(
						"Pokud máte jakékoli požadavky týkající se vynechání či přidání některého zdobení, rádi vám vyjdeme vstříc. Co se však týče samotného rozmístění zdobení, zde má naše cukrářka Viktorie volnou ruku. Finální podoba dortu záleží na jejím citu a jsou to právě její ruce, které z vašeho dortu udělají unikátní umělecké dílo.",
					)}
				</p>
			</Banner>
			<div>
				<div
					class="lg:text-sm mx-auto grid w-fit grid-cols-[repeat(4,auto)] items-center justify-items-center gap-1 gap-y-2 text-left text-2xs sm:gap-2 md:gap-x-4 xl:text-md"
				>
					<div class="font-semibold">Počet porcí</div>
					<div class="font-semibold">Cena</div>
					<div class="font-semibold">Rozměry</div>
					<div class="font-semibold">Podložka</div>

					<div>12 porcí</div>
					<div>2900 Kč</div>
					<div class="flex flex-wrap justify-center gap-1"><span>Ø 18 cm,</span> <span>výška 14 cm</span></div>

					<div>30 cm</div>

					<div>18 porcí</div>
					<div>4200 Kč</div>
					<div class="flex flex-wrap justify-center gap-1"><span>Ø 22 cm,</span> <span>výška 14 cm</span></div>
					<div>30 cm</div>

					<div class="flex flex-wrap justify-center gap-1"><span>25 porcí</span> <span>(dvě patra)</span></div>
					<div>5200 Kč</div>
					<div class="flex flex-wrap justify-center gap-1"><span>Ø 22+15 cm,</span> <span>výška 28 cm</span></div>

					<div>30 cm</div>

					<div>Více porcí</div>
					<div>Individuální cena</div>
					<div>-</div>
					<div>-</div>
				</div>
			</div>
		</section>

		<section>
			<CustomCakeFillings />
		</section>

		<section>
			<CustomCakeFigurines />
		</section>

		<section>
			<Allergies {allergies} />
		</section>

		<section>
			<h2 class="decorative-headline">Vlastní přeprava</h2>
			<div class="flex flex-col justify-center sm:flex-row sm:gap-4 lg:gap-6">
				<ul class="list basis-[calc(50%-0.75rem)]">
					<li>{nbspify("Vyzvednutí vždy osobně na pobočce v Čakovicích nebo na Petřinách")}</li>
					<li>{nbspify("Doručení dortu na místo akce zajišťujeme u třípatrových a větších dortů")}</li>
					<li>
						{nbspify(
							"Dort připravíme do tvrdé kartonové krabice. 12 až 25 porcí - 31x31x32cm. U větších dortů můžeme sdělit velikost krabice operativně",
						)}
					</li>
					<li>
						{nbspify("Dort převážejte pouze v kufru auta nebo na podlaze u sedadla spolujezdce, pokud je prostorná")}
					</li>
				</ul>
				<ul class="list basis-[calc(50%-0.75rem)]">
					<li>{nbspify("Časové rozmezí, kdy vám dort můžeme vydat, je 10:00-18:00")}</li>
					<li>{nbspify("Nikdy dort nedávejte na sedadlo nebo na klín")}</li>
					<li>{nbspify("S dortem je třeba zacházet tak, aby se nikdy nenaklonil")}</li>
					<li>{nbspify("Jeďte plynule, brzděte pozvolna a zatáčejte v malé rychlosti")}</li>
					<li>
						{nbspify("Ničeho se nebojte, zákazníci si už v letních měsících odvezli i několik třípatrových dortů :)")}
					</li>
				</ul>
			</div>
		</section>

		<section class="relative" use:inViewport={{ store: formInViewport }}>
			<h2 class="decorative-headline !mb-6">Poptávka termínu</h2>
			<div id={customCakesEnquiryPathMap[TENANT_LOCALE]} class="absolute top-24 md:top-20"></div>
			<CustomCakeOrderForm {enableDeliveryToAddress} />
		</section>
	</div>

	<CallToActionPanel hide={$formInViewport} type="customCakes" />
</Layout>
